/**
 * @fileoverview Search query state and RFC lookup for the company catalog page.
 * @module hooks/companyCatalog/useCompanyCatalogSearch
 */
import { useCallback, useState } from "react";
import { getCompanyByRFC } from "../../logic/company/index.js";
import { logger, sanitizeError } from "../../utils/logger.js";

/**
 * Tracks the catalog search field, restores the original list when cleared,
 * and resolves a single company by RFC for filtered table views.
 *
 * @param {import('react-router').NavigateFunction} navigate Router navigate used on API auth failures
 * @returns {{
 *   searchQuery: string,
 *   companiesData: Array<Object>,
 *   setCompaniesData: import('react').Dispatch<import('react').SetStateAction<Array<Object>>>,
 *   loading: boolean,
 *   isRfcSearchActive: boolean,
 *   setInitialCompanies: (items: Array<Object>) => void,
 *   updateSearchQuery: (event: import('react').ChangeEvent<HTMLInputElement>) => void,
 *   handleSearch: () => Promise<{ resetPage: boolean }>
 * }} Search state and handlers consumed by catalog controls and pagination
 */
export const useCompanyCatalogSearch = (navigate) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [companiesData, setCompaniesData] = useState([]);
  const [originalCompaniesData, setOriginalCompaniesData] = useState([]);
  const [isRfcSearchActive, setIsRfcSearchActive] = useState(false);
  const [loading, setLoading] = useState(false);

  const setInitialCompanies = useCallback((items) => {
    setCompaniesData(items);
    setOriginalCompaniesData(items);
  }, []);

  const updateSearchQuery = useCallback(
    (event) => {
      setSearchQuery(event.target.value);
      if (event.target.value === "") {
        setIsRfcSearchActive(false);
        setCompaniesData(originalCompaniesData);
      }
    },
    [originalCompaniesData],
  );

  const handleSearch = useCallback(async () => {
    if (searchQuery === "") {
      setIsRfcSearchActive(false);
      setCompaniesData(originalCompaniesData);
      return { resetPage: true };
    }

    setLoading(true);
    try {
      const company = await getCompanyByRFC(searchQuery, navigate);
      if (company) {
        setIsRfcSearchActive(true);
      }
      setCompaniesData(company ? [company] : []);
      return { resetPage: true };
    } catch (error) {
      logger.error("Error fetching company by RFC:", {
        err: sanitizeError(error),
      });
      return { resetPage: true };
    } finally {
      setLoading(false);
    }
  }, [navigate, originalCompaniesData, searchQuery]);

  return {
    searchQuery,
    companiesData,
    setCompaniesData,
    loading,
    isRfcSearchActive,
    setInitialCompanies,
    updateSearchQuery,
    handleSearch,
  };
};
